/*
 * Enrollment form rules, shared by the form in the browser and the /api/enroll route on the server.
 * Keep this file free of server-only imports.
 */

import { SCHEDULE_OPTIONS } from '@/constants/site'

export type EnrollmentField = 'full_name' | 'email' | 'phone' | 'course' | 'schedule' | 'message'

export type EnrollmentValues = Record<EnrollmentField, string>

export type EnrollmentErrors = Partial<Record<EnrollmentField, string>>

export type EnrollmentResponse =
    | { ok: true }
    | { ok: false, message: string, errors?: EnrollmentErrors }

export const ENROLLMENT_LIMITS = {
    name: 100,
    email: 254,
    phone: 40,
    course: 200,
    message: 2000
}

export const ENROLLMENT_FIELDS: Array<EnrollmentField> = ['full_name', 'email', 'phone', 'course', 'schedule', 'message']

const EMAIL_PATTERN = /^[^\s@<>()[\],;:"]+@[^\s@<>()[\],;:"]+\.[^\s@<>()[\],;:"]{2,}$/

const PHONE_PATTERN = /^\+?[0-9()\-.\s]{6,40}$/

const read = (value: unknown): string => typeof value === 'string' ? value.trim() : ''

export const validateEnrollment = (input: Partial<Record<string, unknown>>): { values: EnrollmentValues, errors: EnrollmentErrors } => {
    const values: EnrollmentValues = {
        full_name: read(input.full_name).replace(/\s+/g, ' '),
        email: read(input.email),
        phone: read(input.phone).replace(/\s+/g, ' '),
        course: read(input.course),
        schedule: read(input.schedule),
        message: read(input.message)
    }

    const errors: EnrollmentErrors = {}

    if (values.full_name.length < 2) errors.full_name = "Please enter your full name."
    else if (values.full_name.length > ENROLLMENT_LIMITS.name) errors.full_name = `Please keep your name under ${ENROLLMENT_LIMITS.name} characters.`

    if (!values.email) errors.email = "Please enter your email address."
    else if (values.email.length > ENROLLMENT_LIMITS.email || !EMAIL_PATTERN.test(values.email)) errors.email = "Please enter a valid email address, like name@example.com."

    if (values.phone && !PHONE_PATTERN.test(values.phone)) errors.phone = "Please enter a valid phone number, or leave this field empty."

    if (values.course.length > ENROLLMENT_LIMITS.course) errors.course = "Please choose a course from the list."

    if (values.schedule && !SCHEDULE_OPTIONS.includes(values.schedule)) errors.schedule = "Please choose one of the class times."

    if (values.message.length > ENROLLMENT_LIMITS.message) errors.message = `Please keep your message under ${ENROLLMENT_LIMITS.message} characters.`

    return { values, errors }
}
